import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Spinner } from '@cuemath/comps';
import { useGetEscalationTeams } from 'actions/support-agent';
import { GENERAL_TEAM_NAME, CACHE_KEYS } from '../../constants';
import {
  TeamPickerWrap,
  TeamPickerTrigger,
  TeamPickerTriggerLabel,
  TeamPickerMenu,
  TeamPickerItem,
  FieldErrorMsg,
} from './styled';
import Icon from '../icon';

const TeamPicker = ({ value, onChange, error, disabled }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const {
    data,
    get,
    isProcessing,
    isProcessingFailed,
    error: fetchError,
  } = useGetEscalationTeams();

  useEffect(() => {
    get(CACHE_KEYS.ESCALATION_TEAMS);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = e => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const teams = (Array.isArray(data) ? data : []).filter(
    t => t && t.name !== GENERAL_TEAM_NAME,
  );

  const valueArr = value || [];
  const isSelected = id => valueArr.indexOf(id) !== -1;
  const selectedTeams = teams.filter(t => isSelected(t.id));

  const toggle = useCallback(
    teamId => {
      const next =
        (value || []).indexOf(teamId) !== -1
          ? (value || []).filter(id => id !== teamId)
          : [...(value || []), teamId];
      onChange(next);
    },
    [value, onChange],
  );

  let triggerText;
  let isPlaceholder = false;
  if (isProcessing && teams.length === 0) {
    triggerText = 'Loading teams...';
    isPlaceholder = true;
  } else if (isProcessingFailed) {
    triggerText = fetchError || 'Failed to load teams';
    isPlaceholder = true;
  } else if (selectedTeams.length === 0) {
    triggerText = 'Select teams...';
    isPlaceholder = true;
  } else if (selectedTeams.length === 1) {
    triggerText = selectedTeams[0].name;
  } else {
    triggerText = `${selectedTeams.length} teams selected`;
  }

  return (
    <TeamPickerWrap ref={wrapRef}>
      <TeamPickerTrigger
        type="button"
        $error={!!error}
        disabled={disabled || isProcessingFailed}
        onClick={() => setOpen(o => !o)}
      >
        <TeamPickerTriggerLabel $placeholder={isPlaceholder}>
          {triggerText}
        </TeamPickerTriggerLabel>
        {isProcessing && teams.length === 0 ? (
          <Spinner size={14} />
        ) : (
          <Icon name="chev" size={12} />
        )}
      </TeamPickerTrigger>

      {open && teams.length > 0 && (
        <TeamPickerMenu>
          {teams.map(team => (
            <TeamPickerItem key={team.id}>
              <input
                type="checkbox"
                checked={isSelected(team.id)}
                onChange={() => toggle(team.id)}
              />
              <span>{team.name}</span>
            </TeamPickerItem>
          ))}
        </TeamPickerMenu>
      )}

      {error && <FieldErrorMsg>{error}</FieldErrorMsg>}
    </TeamPickerWrap>
  );
};

export default TeamPicker;
